import { useEffect, useRef } from 'react'

/**
 * Synchronizes state values into a ref for stable access without triggering re-renders.
 *
 * Useful for providing on-demand state access to parent components via callbacks,
 * without causing parent re-renders when state changes.
 *
 * @param state - The state object to synchronize into a ref
 * @param onReady - Optional callback that receives a getter function
 * @returns Getter function that returns current state from ref
 *
 * @example
 * ```tsx
 * const getState = useStateRef(
 *   { drawings, players, targetingMode },
 *   onCanvasStateReady
 * )
 * // Parent can call getState() to get current values without re-rendering
 * ```
 */
export function useStateRef<T>(
	state: T,
	onReady?: (getState: () => T) => void,
): () => T {
	const stateRef = useRef<T>(state)

	// Keep ref in sync with state
	useEffect(() => {
		stateRef.current = state
	}, [state])

	// Provide getter to parent (only runs once when callback provided)
	useEffect(() => {
		if (onReady) {
			const getState = () => stateRef.current
			onReady(getState)
		}
	}, [onReady])

	// Also return getter for local use
	return () => stateRef.current
}
